import {
  Component,
  createSignal,
  For,
  Show,
  createContext,
  useContext,
  ParentComponent,
  onCleanup,
  type JSX,
} from "solid-js";

export type ToastType = "success" | "error" | "warning" | "info";
export type ToastPosition =
  | "top-right"
  | "top-left"
  | "bottom-right"
  | "bottom-left"
  | "top-center"
  | "bottom-center";

interface ToastMessage {
  id: number;
  message: string;
  type: ToastType;
  title?: string;
  duration: number;
  dismissible: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastOptions {
  type?: ToastType;
  title?: string;
  duration?: number;
  dismissible?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextValue {
  show: (message: string, options?: ToastOptions) => number;
  success: (message: string, options?: Omit<ToastOptions, "type">) => number;
  error: (message: string, options?: Omit<ToastOptions, "type">) => number;
  warning: (message: string, options?: Omit<ToastOptions, "type">) => number;
  info: (message: string, options?: Omit<ToastOptions, "type">) => number;
  dismiss: (id: number) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue>();

// Default durations per type
const defaultDurations: Record<ToastType, number> = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 3000,
};

// CSS classes per type - uses .toast-{type} from global.css (colored left border)
// and .alert-{type} from global.css (colored background)
const toastTypeClasses: Record<ToastType, string> = {
  success: "toast-success",
  error: "toast-error",
  warning: "toast-warning",
  info: "toast-info",
};

// Icons per type
const typeIcons: Record<ToastType, string> = {
  success: "\u2713",
  error: "\u2717",
  warning: "\u26A0",
  info: "\u2139",
};

// Inline styles for the container that holds all toast items, positioned by ToastPosition
const containerPositionStyles: Record<ToastPosition, JSX.CSSProperties> = {
  "top-right": {
    position: "fixed",
    top: "24px",
    right: "24px",
    "z-index": "9999",
  },
  "top-left": {
    position: "fixed",
    top: "24px",
    left: "24px",
    "z-index": "9999",
  },
  "bottom-right": {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    "z-index": "9999",
  },
  "bottom-left": {
    position: "fixed",
    bottom: "24px",
    left: "24px",
    "z-index": "9999",
  },
  "top-center": {
    position: "fixed",
    top: "24px",
    left: "50%",
    transform: "translateX(-50%)",
    "z-index": "9999",
  },
  "bottom-center": {
    position: "fixed",
    bottom: "24px",
    left: "50%",
    transform: "translateX(-50%)",
    "z-index": "9999",
  },
};

// Stack layout for the container
const containerBaseStyle: JSX.CSSProperties = {
  display: "flex",
  "flex-direction": "column",
  gap: "8px",
  "pointer-events": "none",
};

// Each toast item style (pointer-events restored so buttons work)
const toastItemStyle: JSX.CSSProperties = {
  "pointer-events": "auto",
};

// Icon style
const iconStyle: JSX.CSSProperties = {
  "font-size": "1.125rem",
  "flex-shrink": "0",
  "line-height": "1",
};

// Content wrapper
const contentStyle: JSX.CSSProperties = {
  flex: "1",
  "min-width": "0",
};

// Title style
const titleStyle: JSX.CSSProperties = {
  "font-weight": "600",
  "font-size": "0.875rem",
  "margin-bottom": "2px",
};

// Message text style
const messageStyle: JSX.CSSProperties = {
  "font-size": "0.875rem",
  "overflow-wrap": "break-word",
  "word-break": "break-word",
};

// Action button style
const actionBtnStyle: JSX.CSSProperties = {
  "margin-top": "4px",
  padding: "2px 8px",
  "font-size": "0.75rem",
  cursor: "pointer",
  background: "transparent",
  border: "1px solid currentColor",
  "border-radius": "4px",
  color: "inherit",
  opacity: "0.8",
};

// Dismiss button style
const dismissBtnStyle: JSX.CSSProperties = {
  "flex-shrink": "0",
  width: "24px",
  height: "24px",
  display: "inline-flex",
  "align-items": "center",
  "justify-content": "center",
  padding: "0",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  "border-radius": "50%",
  "font-size": "0.875rem",
  "line-height": "1",
  color: "inherit",
  opacity: "0.7",
};

interface ToastProviderProps {
  position?: ToastPosition;
  maxToasts?: number;
}

export const ToastProvider: ParentComponent<ToastProviderProps> = (props) => {
  const position = () => props.position ?? "top-right";
  const maxToasts = () => props.maxToasts ?? 5;

  const [toasts, setToasts] = createSignal<ToastMessage[]>([]);
  let toastIdCounter = 0;
  const timeouts = new Map<number, ReturnType<typeof setTimeout>>();

  onCleanup(() => {
    timeouts.forEach((timeout) => clearTimeout(timeout));
    timeouts.clear();
  });

  const dismiss = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timeout = timeouts.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeouts.delete(id);
    }
  };

  const dismissAll = () => {
    setToasts([]);
    timeouts.forEach((timeout) => clearTimeout(timeout));
    timeouts.clear();
  };

  const show = (message: string, options: ToastOptions = {}): number => {
    const type = options.type ?? "info";
    const id = ++toastIdCounter;
    const duration = options.duration ?? defaultDurations[type];
    const dismissible = options.dismissible !== false;

    const toast: ToastMessage = {
      id,
      message,
      type,
      title: options.title,
      duration,
      dismissible,
      action: options.action,
    };

    setToasts((prev) => {
      const newToasts = [...prev, toast];
      // Limit to maxToasts, remove oldest first
      if (newToasts.length > maxToasts()) {
        const removed = newToasts.shift();
        if (removed) {
          const timeout = timeouts.get(removed.id);
          if (timeout) {
            clearTimeout(timeout);
            timeouts.delete(removed.id);
          }
        }
      }
      return newToasts;
    });

    // Auto-dismiss after duration
    if (duration > 0) {
      const timeout = setTimeout(() => {
        dismiss(id);
      }, duration);
      timeouts.set(id, timeout);
    }

    return id;
  };

  const contextValue: ToastContextValue = {
    show,
    success: (message, options) => show(message, { ...options, type: "success" }),
    error: (message, options) => show(message, { ...options, type: "error" }),
    warning: (message, options) => show(message, { ...options, type: "warning" }),
    info: (message, options) => show(message, { ...options, type: "info" }),
    dismiss,
    dismissAll,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {props.children}
      <div
        style={{ ...containerPositionStyles[position()], ...containerBaseStyle }}
        data-component="ToastContainer"
        aria-live="assertive"
        aria-atomic="false"
        role="log">
        <For each={toasts()}>
          {(toast) => (
            <div
              class={`toast ${toastTypeClasses[toast.type]}`}
              style={toastItemStyle}
              role={toast.type === "error" ? "alert" : "status"}
              aria-live={toast.type === "error" ? "assertive" : "polite"}>
              <span style={iconStyle}>{typeIcons[toast.type]}</span>
              <div style={contentStyle}>
                <Show when={toast.title}>
                  <div style={titleStyle}>{toast.title}</div>
                </Show>
                <div style={messageStyle}>{toast.message}</div>
                <Show when={toast.action}>
                  <button
                    class="btn-ghost"
                    style={actionBtnStyle}
                    onClick={() => {
                      toast.action?.onClick();
                      dismiss(toast.id);
                    }}>
                    {toast.action?.label}
                  </button>
                </Show>
              </div>
              <Show when={toast.dismissible}>
                <button
                  style={dismissBtnStyle}
                  onClick={() => dismiss(toast.id)}
                  aria-label="Dismiss">
                  &#x2715;
                </button>
              </Show>
            </div>
          )}
        </For>
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

// Standalone Toast component for legacy usage (without provider)
const [standaloneToasts, setStandaloneToasts] = createSignal<ToastMessage[]>([]);
let standaloneId = 0;

const TOAST_TYPES: Set<string> = new Set(["success", "error", "warning", "info"]);

export const showToast = (
  messageOrType: string,
  typeOrMessage: string | ToastType = "success",
  duration = 3000
): number => {
  // Detect argument order: if first arg is a valid ToastType and second arg is a string,
  // treat as (type, message, duration) -- the legacy CP signature
  let message: string;
  let type: ToastType;
  if (TOAST_TYPES.has(messageOrType) && typeof typeOrMessage === "string" && !TOAST_TYPES.has(typeOrMessage)) {
    // Legacy: showToast("success", "Settings saved!", 3000)
    type = messageOrType as ToastType;
    message = typeOrMessage;
  } else {
    // Standard: showToast("Settings saved!", "success", 3000)
    message = messageOrType;
    type = (TOAST_TYPES.has(typeOrMessage as string) ? typeOrMessage : "success") as ToastType;
  }

  const id = ++standaloneId;
  setStandaloneToasts((prev) => [...prev, { id, message, type, duration, dismissible: true }]);

  if (duration > 0) {
    setTimeout(() => {
      setStandaloneToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }

  return id;
};

export const dismissToast = (id: number) => {
  setStandaloneToasts((prev) => prev.filter((t) => t.id !== id));
};

// Standalone container styles (fixed bottom-right, matching .toast from global.css)
const standaloneContainerStyle: JSX.CSSProperties = {
  position: "fixed",
  bottom: "24px",
  right: "24px",
  "z-index": "9999",
  display: "flex",
  "flex-direction": "column",
  gap: "8px",
  "pointer-events": "none",
};

const standaloneItemStyle: JSX.CSSProperties = {
  "pointer-events": "auto",
};

// Legacy standalone Toast component (for backwards compatibility)
export const Toast: Component = () => {
  return (
    <div style={standaloneContainerStyle} data-component="StandaloneToast" aria-live="assertive" aria-atomic="false" role="log">
      <For each={standaloneToasts()}>
        {(toast) => (
          <div
            class={`toast ${toastTypeClasses[toast.type]}`}
            style={standaloneItemStyle}
            role={toast.type === "error" ? "alert" : "status"}
            aria-live={toast.type === "error" ? "assertive" : "polite"}>
            <span style={iconStyle}>{typeIcons[toast.type]}</span>
            <span style={messageStyle}>{toast.message}</span>
            <button
              style={dismissBtnStyle}
              onClick={() => dismissToast(toast.id)}
              aria-label="Dismiss">
              &#x2715;
            </button>
          </div>
        )}
      </For>
    </div>
  );
};

export default Toast;
